import type {
  WeeklyBriefDetailDTO,
  WeeklyBriefSummaryDTO,
} from '@conversed/rest/src/contracts/weekly-brief/weekly-brief.dto';
import { WeeklyBriefStatus } from '@conversed/rest/src/contracts/weekly-brief/weekly-brief.enums';
import { format } from 'date-fns';
import type { TFunction } from 'i18next';

export const STATUS_LABEL_KEYS: Record<WeeklyBriefStatus, string> = {
  [WeeklyBriefStatus.PendingApproval]:
    'weekly-brief-settings.status.pending-approval',
  [WeeklyBriefStatus.Approved]: 'weekly-brief-settings.status.approved',
  [WeeklyBriefStatus.Sending]: 'weekly-brief-settings.status.sending',
  [WeeklyBriefStatus.Sent]: 'weekly-brief-settings.status.sent',
  [WeeklyBriefStatus.Rejected]: 'weekly-brief-settings.status.rejected',
  [WeeklyBriefStatus.Expired]: 'weekly-brief-settings.status.expired',
  [WeeklyBriefStatus.Skipped]: 'weekly-brief-settings.status.skipped',
  [WeeklyBriefStatus.Failed]: 'weekly-brief-settings.status.failed',
};

export const STATUS_CHIP_CLASSES: Record<WeeklyBriefStatus, string> = {
  [WeeklyBriefStatus.PendingApproval]: 'bg-amber-50 text-amber-700',
  [WeeklyBriefStatus.Approved]: 'bg-amber-50 text-amber-700',
  [WeeklyBriefStatus.Sending]: 'bg-amber-50 text-amber-700',
  [WeeklyBriefStatus.Sent]: 'bg-green-50 text-green-700',
  [WeeklyBriefStatus.Rejected]: 'bg-red-50 text-red-700',
  [WeeklyBriefStatus.Failed]: 'bg-red-50 text-red-700',
  [WeeklyBriefStatus.Expired]: 'bg-gray-100 text-gray-600',
  [WeeklyBriefStatus.Skipped]: 'bg-gray-100 text-gray-600',
};

const AUTO_APPROVED_STATUSES: WeeklyBriefStatus[] = [
  WeeklyBriefStatus.Approved,
  WeeklyBriefStatus.Sending,
  WeeklyBriefStatus.Sent,
];

const REASON_STATUSES: WeeklyBriefStatus[] = [
  WeeklyBriefStatus.Skipped,
  WeeklyBriefStatus.Failed,
  WeeklyBriefStatus.Expired,
];

type TBriefActorFields = Pick<
  WeeklyBriefSummaryDTO,
  'status' | 'approvedBy' | 'rejectedBy'
>;

export const formatActorName = (
  actor: WeeklyBriefSummaryDTO['approvedBy'],
): string => {
  if (!actor) {
    return '';
  }

  return actor.name || actor.email;
};

// Translation key plus its options for the "Approved by" column, or null when
// nobody acted on the brief.
export const getApproverText = (
  brief: TBriefActorFields,
): { key: string; options?: { name: string } } | null => {
  if (brief.status === WeeklyBriefStatus.Rejected) {
    if (!brief.rejectedBy) {
      return null;
    }

    return {
      key: 'weekly-brief-settings.history.rejected-by',
      options: { name: formatActorName(brief.rejectedBy) },
    };
  }

  if (brief.approvedBy) {
    return {
      key: 'weekly-brief-settings.history.approved-by',
      options: { name: formatActorName(brief.approvedBy) },
    };
  }

  if (AUTO_APPROVED_STATUSES.includes(brief.status)) {
    return { key: 'weekly-brief-settings.history.auto-approved' };
  }

  return null;
};

export const shouldShowReason = (status: WeeklyBriefStatus): boolean =>
  REASON_STATUSES.includes(status);

export const formatBriefDateTime = (isoDate: string | null): string => {
  if (!isoDate) {
    return '';
  }

  return format(new Date(isoDate), 'yyyy-MM-dd HH:mm');
};

const withDate = (text: string, isoDate: string | null): string => {
  const date = formatBriefDateTime(isoDate);
  if (!date) {
    return text;
  }

  return `${text}, ${date}`;
};

// The detail lines under the inbox preview in the brief modal; only lines that
// have something to say, so a waiting brief shows none.
export const buildDetailLines = (
  brief: WeeklyBriefDetailDTO,
  t: TFunction,
): string[] => {
  const lines: string[] = [];

  if (brief.status === WeeklyBriefStatus.Rejected && brief.rejectedBy) {
    lines.push(
      withDate(
        t('weekly-brief-settings.history.rejected-by', {
          name: formatActorName(brief.rejectedBy),
        }),
        brief.rejectedAt,
      ),
    );
  } else if (brief.approvedBy) {
    lines.push(
      withDate(
        t('weekly-brief-settings.modal.approved-by', {
          name: formatActorName(brief.approvedBy),
        }),
        brief.approvedAt,
      ),
    );
  } else if (AUTO_APPROVED_STATUSES.includes(brief.status)) {
    lines.push(t('weekly-brief-settings.history.auto-approved'));
  }

  if (brief.sentTo.length > 0) {
    lines.push(
      withDate(
        t('weekly-brief-settings.modal.sent-to-list', {
          recipients: brief.sentTo.join(', '),
        }),
        brief.sentAt,
      ),
    );
  }

  if (shouldShowReason(brief.status) && brief.reason) {
    lines.push(
      t('weekly-brief-settings.modal.reason', { reason: brief.reason }),
    );
  }

  return lines;
};
